import React, { useEffect, useState, useTransition } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import { getMapMetaById } from "../../constants/mapMeta";
import { dateLinesFromMatch } from "./profileRecentMatches";
import ProfileTableShowMoreRow from "./ProfileTableShowMoreRow";
import { EmptyDataState } from "../common/EmptyDataState";
import { CompareArrowsRounded } from "@mui/icons-material";

const MotionBox = motion(Box);

const DELTAS_PAGE_SIZE = 6;

const POSITIVE_COLOR = "#00C853";
const NEGATIVE_COLOR = "#F44336";
const NEUTRAL_COLOR = "rgba(255,255,255,0.85)";

const DELTA_TABLE_COLUMNS =
    "minmax(6.5rem, 2fr) minmax(3.5rem, 0.7fr) minmax(5.5rem, 1.1fr) minmax(4.5rem, 0.85fr)";

const DELTA_TABLE_HEADER_SX = {
    display: "grid",
    gridTemplateColumns: DELTA_TABLE_COLUMNS,
    gap: "0.75rem",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "0.575rem 1.15rem",
    minHeight: "3rem",
    borderRadius: "10px",
    background: `
        linear-gradient(
            180deg,
            rgba(16,18,20,0.96) 0%,
            rgba(10,12,14,0.98) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.04)",
    backdropFilter: "blur(16px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        0 10px 30px rgba(0,0,0,0.28)
    `,
    position: "relative",
    overflow: "hidden",
};

const DELTA_TABLE_ROW_GRID_SX = {
    display: "grid",
    gridTemplateColumns: DELTA_TABLE_COLUMNS,
    gap: "0.75rem",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    px: "1.15rem",
    py: "0.65rem",
};

const HEADER_CELL_SX = {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.42)",
    lineHeight: 1,
    m: 0,
};

const SECONDARY_TEXT_SX = {
    mt: "0.15rem",
    fontSize: "0.78rem",
    fontWeight: 400,
    color: colors.text.secondary,
    lineHeight: 1.2,
};

function formatSignedNumber(value, digits = 0) {
    if (value == null || Number.isNaN(Number(value))) return "-";
    const numericValue = Number(value);
    const fixed = digits > 0 ? numericValue.toFixed(digits) : String(Math.round(numericValue));
    return numericValue > 0 ? `+${fixed}` : fixed;
}

function getValueColor(value) {
    if (value > 0) return POSITIVE_COLOR;
    if (value < 0) return NEGATIVE_COLOR;
    return NEUTRAL_COLOR;
}

function buildStatusChipProps(status) {
    if (status === "applied" || status === "already_applied") {
        return { label: "Applied", sx: { backgroundColor: "rgba(0, 200, 83, 0.16)", color: POSITIVE_COLOR } };
    }
    if (status === "partial_pending") {
        return { label: "Partial", sx: { backgroundColor: "rgba(255, 193, 7, 0.16)", color: "#FFC107" } };
    }
    if (status === "missing_user") {
        return { label: "Missing user", sx: { backgroundColor: "rgba(255, 152, 0, 0.16)", color: "#FF9800" } };
    }
    if (status === "failed") {
        return { label: "Failed", sx: { backgroundColor: "rgba(244, 67, 54, 0.16)", color: NEGATIVE_COLOR } };
    }
    return { label: "Pending", sx: { backgroundColor: "rgba(255,255,255,0.08)", color: "#ffffff" } };
}

function DeltaTableHeader() {
    const columns = [
        { label: "Match", align: "flex-start" },
        { label: "Result", align: "flex-start" },
        { label: "Rating Δ", align: "flex-start" },
        { label: "Status", align: "flex-start" },
    ];

    return (
        <Box
            sx={{
                ...DELTA_TABLE_HEADER_SX,
                display: { xs: "none", md: "grid" },
            }}
        >
            {columns.map(({ label, align }) => (
                <Box
                    key={label}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: align,
                        minWidth: 0,
                    }}
                >
                    <Typography component="span" sx={HEADER_CELL_SX}>
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function PrimaryValue({ children, color = colors.text.primary }) {
    return (
        <Typography
            sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color,
                lineHeight: 1.2,
                fontVariantNumeric: "tabular-nums",
            }}
        >
            {children}
        </Typography>
    );
}

PrimaryValue.propTypes = {
    children: PropTypes.node.isRequired,
    color: PropTypes.string,
};

function SecondaryValue({ children }) {
    return <Typography sx={SECONDARY_TEXT_SX}>{children}</Typography>;
}

SecondaryValue.propTypes = {
    children: PropTypes.node.isRequired,
};

function RatingDeltaValue({ matchRating, before, after, delta }) {
    const hasDelta = delta != null && !Number.isNaN(delta) && delta !== 0;
    const hasTransition = before != null && after != null;

    if (!hasDelta) {
        const baseline =
            matchRating != null
                ? Number(matchRating).toFixed(2)
                : before != null
                    ? Number(before).toFixed(2)
                    : null;

        return (
            <Typography
                sx={{
                    fontSize: "0.82rem",
                    fontWeight: 400,
                    color: colors.text.secondary,
                    lineHeight: 1.2,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {baseline != null ? `${baseline} baseline` : "—"}
            </Typography>
        );
    }

    const signed = formatSignedNumber(delta, 2);
    const isNegative = delta < 0;

    return (
        <Box sx={{ minWidth: 0 }}>
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    padding: "3px 7px",
                    borderRadius: "2px",
                    background: isNegative
                        ? "rgba(244, 67, 54, 0.12)"
                        : "rgba(0, 200, 83, 0.12)",
                    border: `1px solid ${
                        isNegative
                            ? "rgba(244, 67, 54, 0.3)"
                            : "rgba(0, 200, 83, 0.3)"
                    }`,
                    color: getValueColor(delta),
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {isNegative ? "▼" : "▲"} {signed}
            </Box>
            {hasTransition ? (
                <SecondaryValue>
                    {Number(before).toFixed(2)} → {Number(after).toFixed(2)}
                </SecondaryValue>
            ) : null}
        </Box>
    );
}

RatingDeltaValue.propTypes = {
    matchRating: PropTypes.number,
    before: PropTypes.number,
    after: PropTypes.number,
    delta: PropTypes.number,
};

function DeltaTableRow({ row, index }) {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const { dateLine, timeLine } = dateLinesFromMatch(row);
    const mapLabel = getMapMetaById(row.map)?.display || row.map || "Unknown map";
    const status = row.reputation?.status || row.rating3?.status || "pending";
    const chipProps = buildStatusChipProps(status);
    const isWin = row.outcome === "Win";
    const isLoss = row.outcome === "Loss";
    const rating3Delta =
        row.rating3?.before != null && row.rating3?.after != null
            ? Number((row.rating3.after - row.rating3.before).toFixed(2))
            : null;

    const statusChip = (
        <Chip
            label={chipProps.label}
            size="small"
            sx={{
                fontWeight: 700,
                height: "24px",
                ...chipProps.sx,
            }}
        />
    );

    const resultColor = isWin
        ? POSITIVE_COLOR
        : isLoss
            ? NEGATIVE_COLOR
            : colors.text.primary;

    return (
        <MotionBox
            whileHover={isDesktop ? { y: -2 } : undefined}
            sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(18,20,22,0.88) 0%,
                        rgba(12,14,16,0.94) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.04)",
                backdropFilter: "blur(14px)",
                transition: `
                    transform 260ms cubic-bezier(0.22,1,0.36,1),
                    border-color 260ms cubic-bezier(0.22,1,0.36,1),
                    background 260ms cubic-bezier(0.22,1,0.36,1),
                    box-shadow 260ms cubic-bezier(0.22,1,0.36,1)
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.03),
                    0 8px 24px rgba(0,0,0,0.22)
                `,
                "&:hover": {
                    borderColor: "rgba(255,255,255,0.1)",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(22,24,26,0.96),
                            rgba(14,16,18,0.98)
                        )
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        0 14px 40px rgba(0,0,0,0.34)
                    `,
                },
            }}
        >
            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    gap: "0.7rem",
                    px: "0.95rem",
                    py: "0.95rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: "0.88rem",
                                fontWeight: 700,
                                color: colors.text.primary,
                                lineHeight: 1.2,
                            }}
                        >
                            {mapLabel}
                        </Typography>
                        <Typography
                            sx={{
                                mt: "0.25rem",
                                fontSize: "0.78rem",
                                fontWeight: 500,
                                color: "rgba(255,255,255,0.55)",
                                lineHeight: 1.2,
                            }}
                        >
                            {dateLine}
                            {timeLine ? ` · ${timeLine}` : ""}
                        </Typography>
                    </Box>
                    {statusChip}
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.65rem 0.75rem",
                    }}
                >
                    <Box>
                        <Typography sx={HEADER_CELL_SX}>Result</Typography>
                        <Box sx={{ mt: "0.35rem" }}>
                            <PrimaryValue color={resultColor}>
                                {row.outcome || "-"}
                            </PrimaryValue>
                        </Box>
                    </Box>
                    <Box>
                        <Typography sx={HEADER_CELL_SX}>Rating Δ</Typography>
                        <Box sx={{ mt: "0.35rem" }}>
                            <RatingDeltaValue
                                matchRating={row.rating3?.matchRating}
                                before={row.rating3?.before}
                                after={row.rating3?.after}
                                delta={rating3Delta}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    ...DELTA_TABLE_ROW_GRID_SX,
                    display: { xs: "none", md: "grid" },
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            color: colors.text.primary,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mapLabel}
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.15rem",
                            fontSize: "0.78rem",
                            fontWeight: 400,
                            color: colors.text.secondary,
                            lineHeight: 1.2,
                        }}
                    >
                        {dateLine}
                        {timeLine ? ` · ${timeLine}` : ""}
                    </Typography>
                </Box>

                <PrimaryValue color={resultColor}>
                    {row.outcome || "-"}
                </PrimaryValue>

                <RatingDeltaValue
                    matchRating={row.rating3?.matchRating}
                    before={row.rating3?.before}
                    after={row.rating3?.after}
                    delta={rating3Delta}
                />

                <Box sx={{ display: "flex", alignItems: "center" }}>
                    {statusChip}
                </Box>
            </Box>
        </MotionBox>
    );
}

DeltaTableRow.propTypes = {
    row: PropTypes.object.isRequired,
    index: PropTypes.number,
};

export default function ProfileMatchOutcomeDeltas({ rows = [] }) {
    const safeRows = Array.isArray(rows) ? rows : [];
    const [visibleCount, setVisibleCount] = useState(DELTAS_PAGE_SIZE);
    const [isLoadingMore, startLoadingMore] = useTransition();

    useEffect(() => {
        setVisibleCount(DELTAS_PAGE_SIZE);
    }, [rows]);

    const visibleRows = safeRows.slice(0, visibleCount);
    const hasMore = visibleCount < safeRows.length;

    const handleShowMore = () => {
        startLoadingMore(() => {
            setVisibleCount((count) =>
                Math.min(count + DELTAS_PAGE_SIZE, safeRows.length)
            );
        });
    };

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.8rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: colors.text.primary,
                    lineHeight: 1,
                    mb: "0.35rem",
                }}
            >
                Private match outcome deltas
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.725rem",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.42)",
                    mb: "1rem",
                }}
            >
                {safeRows.length} total{" "}
                {safeRows.length === 1 ? "outcome" : "outcomes"}
            </Typography>

            {safeRows.length === 0 ? (
                <EmptyDataState
                    title="No outcome deltas"
                    icon={CompareArrowsRounded}
                    minHeight="10rem"
                    embedded
                >
                    Private match outcome deltas appear after rated matches
                </EmptyDataState>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: "0.65rem", md: "0.5rem" },
                    }}
                >
                    <DeltaTableHeader />
                    {visibleRows.map((row, index) => (
                        <DeltaTableRow
                            key={row.matchId ?? `${row.timestamp}-${index}`}
                            row={row}
                            index={index}
                        />
                    ))}
                    {hasMore || isLoadingMore ? (
                        <ProfileTableShowMoreRow
                            onClick={handleShowMore}
                            isLoading={isLoadingMore}
                        />
                    ) : null}
                </Box>
            )}
        </Box>
    );
}

ProfileMatchOutcomeDeltas.propTypes = {
    rows: PropTypes.array,
};
